import React from 'react';
import { useLocation } from 'react-router-dom';
import { flushAutosaves } from '../lib/autosaveRegistry';

/**
 * Global navigation guard that auto-flushes all pending autosaves.
 *
 * Mount once near the top of the component tree (e.g. inside AppShell).
 *
 * Behaviour:
 *  1. React Router navigation — detects location changes and flushes immediately.
 *  2. Browser close / refresh — `beforeunload` event triggers a flush attempt.
 *
 * This replaces per-page `flushAutosaves()` calls in close handlers.
 */
export function useGlobalAutoFlush() {
  const location = useLocation();
  const prevPathRef = React.useRef(location.pathname);

  // ── Flush on React Router navigation ──
  React.useEffect(() => {
    if (prevPathRef.current !== location.pathname) {
      prevPathRef.current = location.pathname;
      flushAutosaves().catch(() => {});
    }
  }, [location.pathname]);

  // ── Browser close / refresh guard ──
  React.useEffect(() => {
    const handleBeforeUnload = () => {
      try {
        flushAutosaves().catch(() => {});
      } catch {
        // Ignore
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);
}
